// src/components/auth/HostAuthWrapper.tsx
import React, {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {supabase} from '@/services/supabase';
import {useAuth} from '@/context/AuthContext';
import {LoadingSpinner} from '@/components/ui';
import PasswordSetup from '@/pages/auth/PasswordSetup.tsx';
import HostDashboard from '@/pages/HostDashboard';
import {AdminUser, GameHost} from "@/types";

type UserData = AdminUser | GameHost;

interface UserAuthWrapperProps {
    userType?: 'host' | 'admin';
    tableName?: string;
    redirectPath?: string;
    clearPasswordResetFunction?: string;
    children?: React.ReactNode;
}

const UserAuthWrapper: React.FC<UserAuthWrapperProps> = ({
                                                             userType = 'host',
                                                             tableName = 'game_hosts',
                                                             redirectPath = '/host-dashboard',
                                                             clearPasswordResetFunction = 'clear_host_password_reset',
                                                             children
                                                         }) => {
    const {user} = useAuth();
    const [passwordSetupReason, setPasswordSetupReason] = useState<'initial_setup' | 'admin_required' | 'security_update'>('security_update');

    // Fetch host data to check password reset requirements
    const {data: userData, isLoading, error, refetch} = useQuery<UserData>({
        queryKey: [`${userType}-password-status`, user?.email],
        queryFn: async (): Promise<UserData> => {
            if (!user?.email) {
                throw new Error('No user email found');
            }

            console.log(`🔍 Checking ${userType} password reset status for:`, user.email);

            const selectFields = userType === 'host'
                ? `id, email, first_name, last_name, invitation_status, password_reset_required, password_reset_requested_at, password_reset_requested_by`
                : `id, email, first_name, last_name, role, password_reset_required, password_reset_requested_at, password_reset_requested_by`;

            // Get current session for JWT metadata
            const currentSession = await supabase.auth.getSession();
            const userIdFromJWT = userType === 'host'
                ? currentSession?.data?.session?.user?.user_metadata?.host_id
                : currentSession?.data?.session?.user?.user_metadata?.admin_id;

            // Step 1: Try JWT metadata ID first (same for both user types)
            if (userIdFromJWT) {
                console.log(`🔍 Trying ${userType} lookup with JWT ID:`, userIdFromJWT);

                const baseQuery = supabase
                    .from(tableName)
                    .select(selectFields)
                    .eq('id', userIdFromJWT);

                const {data, error} = userType === 'host'
                    ? await baseQuery.is('deleted_at', null).maybeSingle()
                    : await baseQuery.maybeSingle();

                if (data && !error) {
                    console.log(`✅ Found ${userType} using JWT ID`);
                    return data as UserData;
                }
            }

            // Step 2: Fall back to email lookup (same for both user types)
            console.log(`🔍 Trying ${userType} lookup with email:`, user.email);

            const baseEmailQuery = supabase
                .from(tableName)
                .select(selectFields)
                .eq('email', user.email);

            const {data, error} = userType === 'host'
                ? await baseEmailQuery.is('deleted_at', null).maybeSingle()
                : await baseEmailQuery.maybeSingle();

            if (error) {
                console.error(`Error fetching ${userType} data:`, error);
                throw new Error(`Failed to fetch ${userType} data: ${error.message}`);
            }

            if (!data) {
                throw new Error(`${userType} not found`);
            }

            console.log(`✅ Found ${userType} using email lookup`);
            console.log(`📊 ${userType} data:`, {
                email: data.email,
                password_reset_required: data.password_reset_required,
                password_reset_requested_at: data.password_reset_requested_at
            });

            return data as UserData;
        },
        enabled: !!user?.email && (userType === 'host' ? user.role === 'host' : (user.role === 'super_admin' || user.role === 'org_admin')),
        staleTime: 0,
    });

    // Determine the reason for password setup requirement
    useEffect(() => {
        if (userData?.password_reset_required) {
            if (userData.password_reset_requested_by) {
                setPasswordSetupReason('admin_required');
            } else {
                setPasswordSetupReason('security_update');
            }
        }
    }, [userData]);

    // Handle magic link tokens if present (for new users coming from invitation links)
    useEffect(() => {
        const handleMagicLinkAuth = async () => {
            try {
                // Check for auth tokens in URL hash (magic link flow)
                const hashParams = new URLSearchParams(window.location.hash.substring(1));
                const accessToken = hashParams.get('access_token');
                const refreshToken = hashParams.get('refresh_token');

                if (accessToken && refreshToken) {
                    console.log('🔗 Processing magic link tokens for host...');

                    // Clear tokens from URL for security
                    window.history.replaceState(null, '', window.location.pathname);

                    // The AuthProvider should handle this, but we can trigger a refetch
                    // to ensure we have the latest host data after authentication
                    setTimeout(() => {
                        refetch();
                    }, 1000);
                }
            } catch (error) {
                console.error('Error handling magic link tokens:', error);
            }
        };

        handleMagicLinkAuth();
    }, [refetch]);

    // Listen for auth state changes to refetch host data
    useEffect(() => {
        const {data: {subscription}} = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'USER_UPDATED' && session?.user) {
                console.log('🔄 User updated, refetching host data...');
                // Delay refetch slightly to ensure database is updated
                setTimeout(() => {
                    refetch();
                }, 500);
            }
        });

        return () => subscription.unsubscribe();
    }, [refetch]);

    // Show loading state while checking host status
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <LoadingSpinner size="lg"/>
                    <p className="mt-4 text-gray-600">Checking your account status...</p>
                </div>
            </div>
        );
    }

    // Handle error states
    if (error || !userData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md mx-auto text-center p-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-red-800 mb-2">
                            Account Access Error
                        </h2>
                        <p className="text-red-600 mb-4">
                            {error?.message || 'Unable to verify your host account status.'}
                        </p>
                        <div className="space-y-2">
                            <button
                                onClick={() => refetch()}
                                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mr-2"
                            >
                                Retry
                            </button>
                            <button
                                onClick={() => window.location.href = '/login'}
                                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                            >
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Check if host needs password setup/reset
    if (userData?.password_reset_required) {
        return (
            <PasswordSetup
                reason={passwordSetupReason}
                userType={userType}
                redirectPath={redirectPath}
                clearPasswordResetFunction={clearPasswordResetFunction}
            />
        );
    }

    // All checks passed - render children or default component
    if (children) {
        return <>{children}</>;
    }

    // Default fallback for host (backward compatibility)
    return userType === 'host' ? <HostDashboard/> : <div>Dashboard component not provided</div>;
};

export default UserAuthWrapper;